import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAppContext } from 'contexts/AppContext';
import {
  useBudgetQueries,
  useGlobalQuery,
  useTagQuery,
} from 'utils/api/queries';
import { useBudgetMutation } from 'utils/api/mutations';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import PropTypes from 'prop-types';
import {
  getDetailsSheet,
  handlePartialDataUpdate,
  OVERTIME_COLUMNS,
} from 'utils/common';
import { formatCurrency } from 'utils/helper';
import './styles.scss';
import DetailsTable from './DetailsTable';
import useDetailsKeyboardNav from './useDetailsKeyboardNav';

// Custom hooks
import { useFringeLinking } from './hooks/useFringeLinking';
import { useRowOperations } from './hooks/useRowOperations';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useColumnManagement } from './hooks/useColumnManagement';
import { useTotalsCalculation } from './hooks/useTotalsCalculation';
import { useCellPresence } from './hooks/useCellPresence';
import { useMultiCellSelection } from './hooks/useMultiCellSelection';
import { useDetailClipboard } from './hooks/useDetailClipboard';
import { detailRowsAreEquivalent } from './utils/detailRowSync';

// Components
import LinkFringeModal from './LinkFringeModal';
import CellCommentsPopover from './CellCommentsPopover';
import MoveDetailRowModal from './MoveDetailRowModal';

const LIVE_DETAIL_REFRESH_INTERVAL_MS = 1500;

// The API client returns an Axios response today, but keeping this tolerant of
// a direct JSON payload makes the comment markers resilient to either shape.
const getApiCollection = response => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const cellCommentSummaryKey = (budgetId, detailSheetType) => [
  'cell-comment-summary',
  budgetId,
  detailSheetType,
];

const DetailedView = ({
  setNavWidth,
  onKeyUp,
  newDetailViewAdded,
  setNewDetailViewAdded,
  restoreCellState,
  cellPresences,
  onDetailCellFocus,
  isColumnManagerOpen,
  onColumnManagerOpenChange,
  onColumnManagerMetaChange,
  onSuggestionDeleted,
  onRateContextChange,
  mentionableUsers,
}) => {
  const {
    isUserLoggedIn,
    unitTypes,
    useReorderDetailSheet,
    useReorderDetailLineItemSheet,
    userData,
    userProfile,
  } = useAppContext();

  const {
    showDetailedView,
    selectedBudget,
    createSwimlaneDetailsMutation,
    updateSwimlaneDetailsMutation,
    refetchSwimlaneCosts,
    refetchBudgetId,
    hasEditAccess,
    deleteSwimlaneDetailsMutation,
    setShowDetailedView,
    setSwimlanes,
    activeBudget,
    registerEditMenuHandlers,
    showAccountNumbers,
    setShowAccountNumbers,
    setSelectedBudget,
    refetchTabs,
  } = useHomeScreenContext();

  const { level, selectedItem, swimlane } = showDetailedView;
  const [detailsRows, setDetailsRow] = useState({ [selectedItem?.id]: [] });
  const [dirtyRows, setDirtyRows] = useState(() => new Set());
  // Read dirtyRows via ref in the data sync effect so clearing a dirty row
  // does not re-trigger the effect and risk applying stale server data.
  const dirtyRowsRef = useRef(dirtyRows);
  dirtyRowsRef.current = dirtyRows;
  const clearDirtyRow = React.useCallback(rowId => {
    setDirtyRows(prev => {
      const next = new Set(prev);
      next.delete(rowId);
      return next;
    });
  }, []);
  const [caretPoition, setCaretPosition] = useState();
  const [focusEl, setFocusEl] = useState(null);
  const [hoverData, setHoverData] = useState();
  const [highlightedRows] = useState([]);
  const [hoveredFringeId, setHoveredFringeId] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const activeClipboardCellRef = useRef(null);
  const activeCommentCellRef = useRef(null);
  const hasMultipleCellSelectionRef = useRef(false);
  const [commentPopover, setCommentPopover] = useState(null);
  const [moveDetailRowTarget, setMoveDetailRowTarget] = useState(null);
  const [isMovingDetailRows, setIsMovingDetailRows] = useState(false);
  const queryClient = useQueryClient();

  const type =
    level === 0 ? 'swimlane' : level === 1 ? 'line-item' : 'line-item-children';
  const id =
    level === 0
      ? selectedBudget?.budget
      : level === 1
      ? selectedItem?.swimlane
      : selectedItem?.parent;

  const cellCommentSummaryPayload = useMemo(
    () => ({
      budget: selectedBudget?.budget,
      detail_sheet_type: type,
    }),
    [selectedBudget?.budget, type]
  );
  const { data: cellCommentSummaryData } =
    useBudgetQueries.useCellCommentSummaryQuery({
      enabled: Boolean(isUserLoggedIn && selectedBudget?.budget && type),
      payload: cellCommentSummaryPayload,
      config: {
        refetchInterval: 2000,
        // Comment totals must remain accurate while this collaborator's tab is
        // in the background and another collaborator deletes a message.
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: true,
      },
    });
  const commentCounts = useMemo(() => {
    const counts = new Map();
    getApiCollection(cellCommentSummaryData).forEach(summary => {
      counts.set(
        `${summary.detail_sheet_type}:${summary.row_id}:${summary.column_key}`,
        summary.count
      );
    });
    return counts;
  }, [cellCommentSummaryData]);

  const cellCommentThreadPayload = useMemo(() => {
    if (!commentPopover?.target || !selectedBudget?.budget) return null;
    return {
      budget: selectedBudget.budget,
      detail_sheet_type: commentPopover.target.detailSheetType,
      row_id: commentPopover.target.rowId,
      column_key: commentPopover.target.columnKey,
    };
  }, [commentPopover?.target, selectedBudget?.budget]);
  const { data: cellCommentThreadData, isLoading: isCellCommentThreadLoading } =
    useBudgetQueries.useCellCommentsQuery({
      enabled: Boolean(cellCommentThreadPayload),
      payload: cellCommentThreadPayload,
      config: {
        refetchInterval: 2000,
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: true,
      },
    });
  const cellCommentThreadReadKey = useMemo(
    () =>
      getApiCollection(cellCommentThreadData)
        .map(
          comment =>
            `${comment.id}:${comment.updated_on || comment.created_on || ''}`
        )
        .join(','),
    [cellCommentThreadData]
  );
  const {
    data: moveDetailRowCategoriesData,
    isLoading: isMoveDetailRowCategoriesLoading,
  } = useBudgetQueries.useBudgetCategoriesQuery({
    budgetId: selectedBudget?.budget,
    enabled: Boolean(moveDetailRowTarget && selectedBudget?.budget),
    config: { staleTime: 0 },
  });
  const createCellCommentMutation =
    useBudgetMutation.useCreateCellCommentMutation();
  const updateCellCommentMutation =
    useBudgetMutation.useUpdateCellCommentMutation();
  const deleteCellCommentMutation =
    useBudgetMutation.useDeleteCellCommentMutation();
  const markCommentNotificationsReadMutation =
    useBudgetMutation.useMarkCommentNotificationsReadMutation();
  const moveDetailRowMutation = useBudgetMutation.useMoveDetailRowMutation();
  const updateBudgetMutation = useBudgetMutation.useCreateBudgetMutation();

  const swimlaneQueryKey = useMemo(
    () => ['get-swimlanes-in-budget', { id, type }],
    [id, type]
  );

  const cellPresence = useCellPresence({
    budgetId: selectedBudget?.budget,
    detailSheetType: type,
    presences: cellPresences,
    enabled: Boolean(
      isUserLoggedIn && selectedBudget?.budget && selectedItem?.id
    ),
  });

  const getCommentCellElement = useCallback(target => {
    if (!target || typeof document === 'undefined') return null;
    return document.querySelector(
      `[data-detail-row-id="${target.rowId}"][data-detail-column-key="${target.columnKey}"]`
    );
  }, []);

  const handleCommentCellFocus = useCallback(target => {
    if (!target?.rowId || !target?.columnKey) return;
    activeCommentCellRef.current = target;
  }, []);

  const openCellComments = useCallback(
    (target, { toggle = false } = {}) => {
      const nextTarget = target || activeCommentCellRef.current;
      if (!nextTarget?.rowId || !nextTarget?.columnKey) {
        toast.info('Select a single detailed budget cell to add a comment.');
        return;
      }
      if (hasMultipleCellSelectionRef.current) {
        toast.info('Select a single detailed budget cell to add a comment.');
        return;
      }

      if (
        toggle &&
        commentPopover?.target?.rowId === nextTarget.rowId &&
        commentPopover.target.columnKey === nextTarget.columnKey &&
        commentPopover.target.detailSheetType === nextTarget.detailSheetType
      ) {
        setCommentPopover(null);
        return;
      }

      const cellElement = getCommentCellElement(nextTarget);
      const rect = cellElement?.getBoundingClientRect?.();
      const anchorRect =
        nextTarget.anchorRect ||
        (rect
          ? {
              bottom: rect.bottom,
              left: rect.left,
              right: rect.right,
              top: rect.top,
            }
          : null);
      if (!anchorRect) return;

      const normalizedTarget = {
        ...nextTarget,
        anchorElement: cellElement,
        anchorRect,
      };
      activeCommentCellRef.current = normalizedTarget;
      setCommentPopover({ target: normalizedTarget });
    },
    [commentPopover?.target, getCommentCellElement]
  );

  const closeCellComments = useCallback(() => {
    const target = commentPopover?.target;
    setCommentPopover(null);
    window.setTimeout(() => {
      const cellElement = getCommentCellElement(target);
      cellElement
        ?.querySelector?.('.cell-comment-badge, .col-content')
        ?.focus?.();
    }, 0);
  }, [commentPopover?.target, getCommentCellElement]);

  const invalidateCellComments = useCallback(
    async target => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: cellCommentSummaryKey(
            selectedBudget?.budget,
            target.detailSheetType
          ),
          exact: true,
          refetchType: 'active',
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'cell-comments',
            selectedBudget?.budget,
            target.detailSheetType,
            target.rowId,
            target.columnKey,
          ],
          exact: true,
          refetchType: 'active',
        }),
      ]);
    },
    [queryClient, selectedBudget?.budget]
  );

  const createCellComment = useCallback(
    async (body, mentionedUserIds = []) => {
      const target = commentPopover?.target;
      if (!target) return null;
      try {
        const response = await createCellCommentMutation.mutateAsync({
          budget: selectedBudget?.budget,
          detail_sheet_type: target.detailSheetType,
          row_id: target.rowId,
          column_key: target.columnKey,
          body,
          mentioned_user_ids: mentionedUserIds,
        });
        // Do not increment the cached count here. The two-second summary poll
        // can already have the new server total by the time this mutation
        // resolves, which would briefly turn one comment into two.
        await invalidateCellComments(target);
        return response;
      } catch (error) {
        toast.error('Unable to add this comment. Please try again.');
        throw error;
      }
    },
    [
      commentPopover?.target,
      createCellCommentMutation,
      invalidateCellComments,
      selectedBudget?.budget,
    ]
  );

  const updateCellComment = useCallback(
    async (commentId, body, mentionedUserIds = []) => {
      const target = commentPopover?.target;
      if (!target) return null;
      try {
        const response = await updateCellCommentMutation.mutateAsync({
          id: commentId,
          body,
          mentioned_user_ids: mentionedUserIds,
        });
        await invalidateCellComments(target);
        return response;
      } catch (error) {
        toast.error('Unable to update this comment. Please try again.');
        throw error;
      }
    },
    [commentPopover?.target, invalidateCellComments, updateCellCommentMutation]
  );

  useEffect(() => {
    if (!cellCommentThreadPayload) return;
    markCommentNotificationsReadMutation.mutate({
      budget: cellCommentThreadPayload.budget,
      comment_scope: 'cell',
      detail_sheet_type: cellCommentThreadPayload.detail_sheet_type,
      row_id: cellCommentThreadPayload.row_id,
      column_key: cellCommentThreadPayload.column_key,
    });
    // The target change, rather than the two-second thread poll, defines a view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cellCommentThreadPayload?.budget,
    cellCommentThreadPayload?.column_key,
    cellCommentThreadPayload?.detail_sheet_type,
    cellCommentThreadPayload?.row_id,
    cellCommentThreadReadKey,
  ]);

  const deleteCellComment = useCallback(
    async commentId => {
      const target = commentPopover?.target;
      if (!target) return null;
      try {
        const response = await deleteCellCommentMutation.mutateAsync(commentId);
        await invalidateCellComments(target);
        return response;
      } catch (error) {
        toast.error('Unable to delete this comment. Please try again.');
        throw error;
      }
    },
    [commentPopover?.target, deleteCellCommentMutation, invalidateCellComments]
  );

  const { data, dataUpdatedAt, isError, refetch } =
    useBudgetQueries.useSwimlaneQuery({
      enabled: Boolean(isUserLoggedIn && type && id),
      payload: { id, type },
      config: {
        refetchInterval:
          dirtyRows.size === 0 ? LIVE_DETAIL_REFRESH_INTERVAL_MS : false,
        refetchIntervalInBackground: false,
      },
    });

  const requestMoveDetailRow = useCallback(
    itemOrItems => {
      const rows = (
        Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems]
      ).filter(Boolean);
      if (
        !rows.length ||
        rows.some(
          row =>
            !row?.id ||
            String(row.id).startsWith('draft-') ||
            String(row.id).startsWith('temp-')
        )
      ) {
        toast.info('Save this line item before moving it.');
        return;
      }
      if (
        rows.some(row => dirtyRows.has(row.id) || dirtyRows.has(String(row.id)))
      ) {
        toast.info(
          'Wait for all selected line items to finish saving before moving them.'
        );
        return;
      }
      setMoveDetailRowTarget(rows);
    },
    [dirtyRows]
  );

  const moveDetailRow = useCallback(
    async destinationCategoryId => {
      const rows = (
        Array.isArray(moveDetailRowTarget)
          ? moveDetailRowTarget
          : [moveDetailRowTarget]
      ).filter(row => row?.id);
      if (!rows.length || !destinationCategoryId || isMovingDetailRows) return;

      setIsMovingDetailRows(true);
      try {
        for (const row of rows) {
          // Serializing moves preserves the selected rows' visual order at the
          // bottom of the destination detail view.
          // eslint-disable-next-line no-await-in-loop
          await moveDetailRowMutation.mutateAsync({
            id: row.id,
            category: destinationCategoryId,
          });
        }
        const movedRowIds = new Set(rows.map(row => String(row.id)));
        setDetailsRow(previous => {
          const currentRows = previous[selectedItem?.id] || [];
          const nextRows = currentRows
            .filter(currentRow => !movedRowIds.has(String(currentRow?.id)))
            .map((currentRow, position) => ({
              ...currentRow,
              position,
              row_order: position + 1,
            }));
          return { ...previous, [selectedItem?.id]: nextRows };
        });
        rows.forEach(row => clearDirtyRow(row.id));
        queryClient.invalidateQueries({
          queryKey: ['get-swimlanes-in-budget'],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: ['cell-comment-summary'],
          exact: false,
        });
        refetch();
        refetchBudgetId?.();
        refetchSwimlaneCosts?.({ budgetId: selectedBudget?.budget });
        onRateContextChange?.({ focusItems: [] });
        setMoveDetailRowTarget(null);
      } catch (error) {
        queryClient.invalidateQueries({
          queryKey: ['get-swimlanes-in-budget'],
          exact: false,
        });
        refetch();
        setMoveDetailRowTarget(null);
        toast.error(
          rows.length > 1
            ? 'Unable to move all selected line items. Please try again.'
            : 'Unable to move this line item. Please try again.'
        );
      } finally {
        setIsMovingDetailRows(false);
      }
    },
    [
      clearDirtyRow,
      moveDetailRowMutation,
      moveDetailRowTarget,
      isMovingDetailRows,
      onRateContextChange,
      queryClient,
      refetch,
      refetchBudgetId,
      refetchSwimlaneCosts,
      selectedBudget?.budget,
      selectedItem?.id,
    ]
  );

  const { data: budgetGlobalsResponse } = useGlobalQuery.useGlobalsQuery({
    filters: { instance_type: 'budget', active_id: activeBudget },
    enabled: Boolean(activeBudget),
  });
  const budgetGlobals = useMemo(
    () =>
      budgetGlobalsResponse?.data?.data || budgetGlobalsResponse?.data || [],
    [budgetGlobalsResponse]
  );
  const { data: budgetTagsResponse, refetch: refetchBudgetTags } =
    useTagQuery.useTagsQuery({
      filters: { active_id: activeBudget },
      enabled: Boolean(activeBudget),
    });
  const budgetTags = useMemo(
    () => budgetTagsResponse?.data || [],
    [budgetTagsResponse]
  );

  // Only apply refetch results that are newer than what we last applied, so older
  // in-flight refetches cannot overwrite newer data when they complete later
  const lastAppliedDataUpdatedAtRef = useRef(0);
  // Track which item we've already ensured an empty row for, so we don't
  // call addEmptyRow on every refetch — only on the first data load per item.
  const ensuredEmptyRowForRef = useRef(null);

  useEffect(() => {
    lastAppliedDataUpdatedAtRef.current = 0;
  }, [selectedItem?.id, id, type]);

  useEffect(() => {
    activeCommentCellRef.current = null;
    setCommentPopover(null);
  }, [selectedItem?.id, type]);

  const closeStaleDetailView = useCallback(
    ({ removeCurrentColumn = false } = {}) => {
      setShowDetailedView({ show: false });
      setSwimlanes(currentSwimlanes => {
        if (!Array.isArray(currentSwimlanes)) return currentSwimlanes;
        const columnsToKeep = Math.max(
          removeCurrentColumn ? level : level + 1,
          1
        );
        return currentSwimlanes.slice(0, columnsToKeep);
      });
    },
    [level, setShowDetailedView, setSwimlanes]
  );

  useEffect(() => {
    if (!selectedItem?.id) return;

    if (isError) {
      closeStaleDetailView({ removeCurrentColumn: true });
      return;
    }

    if (!Array.isArray(data?.data)) return;

    const selectedItemStillExists = data.data.some(
      item => item?.id?.toString() === selectedItem.id.toString()
    );

    if (!selectedItemStillExists) {
      closeStaleDetailView();
    }
  }, [closeStaleDetailView, data, isError, selectedItem?.id]);

  // Custom hooks
  const fringeLinking = useFringeLinking({
    selectedBudget,
    selectedItem,
    level,
    detailsRows,
    refetch,
  });

  const columnManagement = useColumnManagement({
    selectedBudget,
    unitTypes,
    detailsRows,
    selectedItem,
    setNavWidth,
    fringeGroups: fringeLinking.fringeGroups,
    showAccountNumbers,
    setShowAccountNumbers,
  });

  const handleUpdateOvertimeSettings = useCallback(
    async (columnKey, settings) => {
      const overtimeColumn = OVERTIME_COLUMNS.find(
        ({ slot }) => `ot${slot}_quantity` === columnKey
      );
      if (!overtimeColumn || !selectedBudget?.budget || !hasEditAccess()) {
        return;
      }

      const prefix = `ot${overtimeColumn.slot}`;
      const nextSettings = {
        [`${prefix}_label`]: String(settings?.label ?? '')
          .trim()
          .slice(0, 80),
        [`${prefix}_multiplier`]: settings?.multiplier,
        [`${prefix}_start`]: settings?.start,
        [`${prefix}_end`]: settings?.end,
      };
      const previousSettings = Object.keys(nextSettings).reduce(
        (values, fieldName) => ({
          ...values,
          [fieldName]: selectedBudget?.[fieldName],
        }),
        {}
      );

      setSelectedBudget(currentBudget => ({
        ...currentBudget,
        ...nextSettings,
      }));

      try {
        await updateBudgetMutation.mutateAsync({
          id: selectedBudget.budget,
          ...nextSettings,
        });
        queryClient.invalidateQueries({
          queryKey: ['get-swimlanes-in-budget'],
          exact: false,
        });
        await Promise.all([refetch(), refetchBudgetId?.(), refetchTabs?.()]);
      } catch (_error) {
        setSelectedBudget(currentBudget => ({
          ...currentBudget,
          ...previousSettings,
        }));
        toast.error('Could not save the overtime settings.');
      }
    },
    [
      hasEditAccess,
      queryClient,
      refetch,
      refetchBudgetId,
      refetchTabs,
      selectedBudget,
      setSelectedBudget,
      updateBudgetMutation,
    ]
  );

  useEffect(() => {
    onColumnManagerMetaChange({
      hiddenColumnCount: columnManagement.hiddenColumnCount,
    });
  }, [columnManagement.hiddenColumnCount, onColumnManagerMetaChange]);

  const rowOperations = useRowOperations({
    selectedItem,
    selectedBudget,
    level,
    type:
      level === 0
        ? 'swimlane'
        : level === 1
        ? 'line-item'
        : 'line-item-children',
    swimlane,
    detailsRows,
    setDetailsRow,
    refetchSwimlaneCosts,
    refetchBudgetId,
    createSwimlaneDetailsMutation,
    updateSwimlaneDetailsMutation,
    deleteSwimlaneDetailsMutation,
    columns: columnManagement.columns,
    setDirtyRows,
    clearDirtyRow,
    onMoveRow: requestMoveDetailRow,
    onSuggestionDeleted,
    onRateContextChange,
  });
  const { addEmptyRow } = rowOperations;

  useEffect(() => {
    if (!newDetailViewAdded || !selectedItem?.id || !hasEditAccess()) return;

    const currentRows = detailsRows[selectedItem.id] || [];
    if (currentRows.length > 0) return;

    addEmptyRow();
  }, [
    detailsRows,
    hasEditAccess,
    addEmptyRow,
    newDetailViewAdded,
    selectedItem?.id,
  ]);

  const dragAndDrop = useDragAndDrop({
    selectedItem,
    detailsRows,
    setDetailsRow,
    queryKey: swimlaneQueryKey,
    detailSheetType: type,
    refetchSwimlaneCosts,
    selectedBudget,
    useReorderDetailSheet,
    useReorderDetailLineItemSheet,
  });

  const totalsCalculation = useTotalsCalculation({
    detailsRows,
    selectedItem,
  });

  const handleClipboardSelectionChange = useCallback(nextSelection => {
    if (!nextSelection?.columnKey || !nextSelection?.rowKeys?.length) return;

    activeClipboardCellRef.current = {
      columnKey: nextSelection.columnKey,
      rowKeys: [...nextSelection.rowKeys],
    };
  }, []);

  const multiCellSelection = useMultiCellSelection({
    rows: totalsCalculation.updatedRows,
    columns: columnManagement.updatedColumns,
    hiddenColumn: columnManagement.hiddenColumn,
    detailSheetType: type,
    canEdit: hasEditAccess(),
    addRow: rowOperations.addRow,
    budgetGlobals,
    overtimeConfig: selectedBudget,
    onClipboardSelectionChange: handleClipboardSelectionChange,
  });
  hasMultipleCellSelectionRef.current =
    multiCellSelection.selectedCellKeys.size > 1;

  const handleAddCommentFromMenu = useCallback(() => {
    openCellComments(activeCommentCellRef.current);
  }, [openCellComments]);

  useDetailClipboard({
    rows: totalsCalculation.updatedRows,
    columns: columnManagement.updatedColumns,
    hiddenColumn: columnManagement.hiddenColumn,
    activeCellRef: activeClipboardCellRef,
    selection: multiCellSelection.selection,
    addRow: rowOperations.addRow,
    clearSelection: multiCellSelection.clearSelection,
    canEdit: hasEditAccess(),
    registerEditMenuHandlers,
    budgetGlobals,
    overtimeConfig: selectedBudget,
    onAddComment: handleAddCommentFromMenu,
  });

  const { activeCell, updateActiveSelectedCells } = cellPresence;
  const { selection, getPresenceCellsForCell } = multiCellSelection;

  const handleClipboardCellFocus = useCallback(cell => {
    activeClipboardCellRef.current = {
      ...cell,
      rowKeys: cell?.rowKey ? [cell.rowKey] : [],
    };
  }, []);

  useEffect(() => {
    if (!activeCell) return;

    updateActiveSelectedCells(getPresenceCellsForCell(activeCell));
  }, [
    activeCell,
    getPresenceCellsForCell,
    selection,
    updateActiveSelectedCells,
  ]);

  useEffect(() => {
    if (data) {
      setHoverData(data);
    }
  }, [data]);

  // Effects for data synchronization (dirty rows keep local version to avoid overwriting unsaved edits).
  // Only apply when dataUpdatedAt is newer than last applied, so older refetch responses cannot overwrite newer data.
  // dirtyRows is read via ref (dirtyRowsRef) so clearing a dirty row after a successful PATCH
  // does not re-trigger this effect and risk applying stale server data for other rows.
  useEffect(() => {
    if (!data || !selectedItem?.id) return;
    const updatedAt = dataUpdatedAt ?? 0;
    if (updatedAt <= lastAppliedDataUpdatedAtRef.current) return;
    lastAppliedDataUpdatedAtRef.current = updatedAt;

    const detail = getDetailsSheet(
      data?.data?.find(item => item?.id === selectedItem?.id),
      type
    );

    // Use the callback form so we always read the latest state via `prev`,
    // avoiding stale-closure bugs where detailsRows from the render that
    // scheduled this effect is already outdated.
    // Capture the merged result so we can check for placeholders below.
    let mergedRows;
    setDetailsRow(prev => {
      const existingRows = prev[selectedItem?.id] || [];
      mergedRows = handlePartialDataUpdate(
        existingRows,
        detail,
        selectedItem,
        type,
        dirtyRowsRef.current
      );
      if (detailRowsAreEquivalent(existingRows, mergedRows)) return prev;
      return { ...prev, [selectedItem?.id]: mergedRows };
    });

    // After syncing server data, ensure one placeholder row exists.
    // Only do this once per selectedItem to avoid creating duplicates on refetches.
    // Check `detail` (server data) — `mergedRows` is set inside a setState
    // updater and is not reliably available synchronously here.
    if (hasEditAccess() && ensuredEmptyRowForRef.current !== selectedItem?.id) {
      const hasPlaceholder = detail?.some(row => row?.is_placeholder);
      if (!detail?.length || !hasPlaceholder) {
        rowOperations.addEmptyRow();
      }
      ensuredEmptyRowForRef.current = selectedItem?.id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataUpdatedAt, selectedItem, type]);

  useEffect(() => {
    if (newDetailViewAdded) {
      setCaretPosition('item,0');
      setTimeout(() => {
        document.getElementsByName(`item,0`)[0]?.focus();
      }, 0);
      setNewDetailViewAdded(false);
    }
  }, [newDetailViewAdded, setNewDetailViewAdded]);

  const restoredCellKeyRef = useRef('');

  useEffect(() => {
    if (
      !restoreCellState?.rowId ||
      !restoreCellState?.columnKey ||
      !selectedItem?.id
    ) {
      return undefined;
    }

    if (
      restoreCellState?.detailItemId &&
      restoreCellState.detailItemId.toString() !== selectedItem.id.toString()
    ) {
      return undefined;
    }

    const restoreKey = [
      selectedItem.id,
      restoreCellState.rowId,
      restoreCellState.columnKey,
      restoreCellState.focusToken || '',
    ].join(':');
    if (restoredCellKeyRef.current === restoreKey) return undefined;

    const timer = setTimeout(() => {
      const cell = document.querySelector(
        `[data-detail-row-id="${restoreCellState.rowId}"][data-detail-column-key="${restoreCellState.columnKey}"]`
      );
      const focusable = cell?.querySelector?.(
        'input, textarea, [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
      );

      if (!focusable) return;

      focusable.focus();
      focusable.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      if (typeof focusable.setSelectionRange === 'function') {
        const cursorPosition = String(focusable.value || '').length;
        focusable.setSelectionRange(cursorPosition, cursorPosition);
      }
      if (restoreCellState.openComment) {
        const rect = cell.getBoundingClientRect();
        openCellComments({
          detailSheetType: type,
          rowId: restoreCellState.rowId,
          columnKey: restoreCellState.columnKey,
          anchorRect: {
            bottom: rect.bottom,
            left: rect.left,
            right: rect.right,
            top: rect.top,
          },
        });
      }
      restoredCellKeyRef.current = restoreKey;
    }, 250);

    return () => clearTimeout(timer);
  }, [
    dataUpdatedAt,
    detailsRows,
    openCellComments,
    restoreCellState,
    selectedItem?.id,
    type,
  ]);

  // Keyboard navigation
  const handleKeyUp = useDetailsKeyboardNav({
    detailsRows,
    selectedItem,
    type,
    createSwimlaneDetailsMutation,
    swimlane,
    setDetailsRow,
    reorderRows: dragAndDrop.reorderRows,
    refetch,
    refetchBudgetId,
    setCaretPosition,
    caretPoition,
    onKeyUp,
  });

  return (
    <>
      <DetailsTable
        updatedColumns={columnManagement.updatedColumns}
        hiddenColumn={columnManagement.hiddenColumn}
        detailsRows={detailsRows}
        selectedItem={selectedItem}
        detailSheetType={type}
        updatedRows={totalsCalculation.updatedRows}
        addRow={rowOperations.addRow}
        onItemFieldCommit={rowOperations.commitRateContextChange}
        handleKeyUp={handleKeyUp}
        focus={columnManagement.focus}
        hasEditAccess={hasEditAccess}
        addEmptyRow={rowOperations.addEmptyRow}
        handleRowAction={rowOperations.handleRowAction}
        focusEl={focusEl}
        setFocusEl={setFocusEl}
        fringeRowCount={fringeLinking.fringeRowCount}
        refreshLinkedFringes={fringeLinking.refreshLinkedFringes}
        checkedRows={fringeLinking.checkedRows}
        selectedFringe={fringeLinking.selectedFringe}
        handleCheckboxChange={fringeLinking.handleCheckboxChange}
        fringeRows={fringeLinking.fringeRows}
        hoverData={hoverData}
        highlightedRows={highlightedRows}
        fringeGroups={{
          ...fringeLinking.fringeGroups,
          setter: fringeLinking.setFringeGroups,
        }}
        hoveredFringeId={hoveredFringeId}
        setHoveredFringeId={setHoveredFringeId}
        hoveredRowId={hoveredRowId}
        setHoveredRowId={setHoveredRowId}
        onDragStart={dragAndDrop.onDragStart}
        onDragEnd={dragAndDrop.onDragEnd}
        hasFringeTotalColumn={columnManagement.hasFringeTotalColumn}
        hasAppliedFringe={totalsCalculation.hasAppliedFringe}
        AppliedFringe={totalsCalculation.AppliedFringe}
        detailsTotal={totalsCalculation.detailsTotal}
        usesExchangeRates={totalsCalculation.usesExchangeRates}
        actualTotal={totalsCalculation.actualTotal}
        hasActualTotal={totalsCalculation.hasActualTotal}
        selectedBudget={selectedBudget}
        formatCurrency={formatCurrency}
        setShowModal={fringeLinking.handleOpenModal}
        setSelectedFringe={fringeLinking.setSelectedFringe}
        cellPresences={cellPresence.presences}
        onCellPresenceFocus={cellPresence.startCellPresence}
        onCellPresenceBlur={cellPresence.scheduleClearCellPresence}
        onDetailCellFocus={onDetailCellFocus}
        onClipboardCellFocus={handleClipboardCellFocus}
        commentCounts={commentCounts}
        onCommentCellFocus={handleCommentCellFocus}
        onOpenCellComments={openCellComments}
        multiCellSelection={multiCellSelection}
        budgetGlobals={budgetGlobals}
        budgetTags={budgetTags}
        refetchBudgetTags={refetchBudgetTags}
        columnManagerColumns={columnManagement.columnManagerColumns}
        isColumnManagerOpen={isColumnManagerOpen}
        onColumnManagerOpenChange={onColumnManagerOpenChange}
        onToggleColumn={columnManagement.toggleManagedColumn}
        onReorderColumns={columnManagement.reorderColumns}
        onReorderColumnsByKey={columnManagement.reorderColumnsByKey}
        onApplyColumnOrder={columnManagement.applyManagedColumnOrder}
        onResizeColumns={columnManagement.resizeColumns}
        onResetColumns={columnManagement.resetColumnPreferences}
        onUpdateOvertimeSettings={handleUpdateOvertimeSettings}
      />

      {commentPopover?.target && (
        <CellCommentsPopover
          anchorElement={commentPopover.target.anchorElement}
          anchorRect={commentPopover.target.anchorRect}
          comments={getApiCollection(cellCommentThreadData)}
          currentUser={{
            ...userData,
            profile_image:
              userProfile?.userprofile?.profile_image ||
              userData?.profile_image,
          }}
          isLoading={isCellCommentThreadLoading}
          mentionableUsers={mentionableUsers}
          isSubmitting={
            createCellCommentMutation.isLoading ||
            updateCellCommentMutation.isLoading ||
            deleteCellCommentMutation.isLoading
          }
          onClose={closeCellComments}
          onCreate={createCellComment}
          onDelete={deleteCellComment}
          onUpdate={updateCellComment}
        />
      )}
      <MoveDetailRowModal
        categories={getApiCollection(moveDetailRowCategoriesData)}
        isLoading={isMoveDetailRowCategoriesLoading}
        isMoving={isMovingDetailRows}
        onConfirm={moveDetailRow}
        onHide={() => setMoveDetailRowTarget(null)}
        rows={moveDetailRowTarget || []}
        show={Boolean(moveDetailRowTarget)}
        sourceCategoryId={selectedItem?.id}
      />

      <LinkFringeModal
        show={fringeLinking.showModal}
        onHide={fringeLinking.handleModalClose}
        selectedFringe={fringeLinking.selectedFringe}
        filteredFringe={fringeLinking.filteredFringe}
        isLoading={fringeLinking.isLoading}
        filteredRows={fringeLinking.filteredRows}
        rangeColumns={fringeLinking.rangeColumns}
        rangeCheckedRows={fringeLinking.rangeCheckedRows}
        onRangeCheckboxChange={fringeLinking.handleRangeCheckboxChange}
        onRangeSelectAll={fringeLinking.handleRangeSelectAll}
        onAddRange={fringeLinking.handleAddRange}
        onRemoveRange={fringeLinking.handleRemoveRange}
        onConfirm={fringeLinking.handleLinkFringes}
      />
    </>
  );
};

DetailedView.propTypes = {
  setNavWidth: PropTypes.func,
  onKeyUp: PropTypes.func,
  newDetailViewAdded: PropTypes.bool,
  setNewDetailViewAdded: PropTypes.func.isRequired,
  restoreCellState: PropTypes.object,
  cellPresences: PropTypes.array,
  onDetailCellFocus: PropTypes.func,
  isColumnManagerOpen: PropTypes.bool,
  onColumnManagerOpenChange: PropTypes.func,
  onColumnManagerMetaChange: PropTypes.func,
  onSuggestionDeleted: PropTypes.func,
  onRateContextChange: PropTypes.func,
  mentionableUsers: PropTypes.array,
};

DetailedView.defaultProps = {
  setNavWidth: null,
  onKeyUp: null,
  newDetailViewAdded: false,
  restoreCellState: null,
  cellPresences: [],
  onDetailCellFocus: () => null,
  isColumnManagerOpen: false,
  onColumnManagerOpenChange: () => null,
  onColumnManagerMetaChange: () => null,
  onSuggestionDeleted: () => null,
  onRateContextChange: () => null,
  mentionableUsers: [],
};

export default DetailedView;
